import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { FormField, useForm, useSubmitForm } from 'api/aws/form-builder/useFormBuilder'
import { useClickUpFolders, useClickUpLists, useClickUpSpaces } from 'api/aws/strategy-builder/useStrategyBuilder'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

// ── Conditional Visibility ──────────────────────────────────────────────────

function evaluateFieldVisibility(fields: FormField[], responses: Record<string, any>): Set<string> {
  const visibleFields = new Set(fields.map(f => f.fieldId))

  for (let pass = 0; pass < 10; pass++) {
    let changed = false
    for (const field of fields) {
      if (!field.conditionalRule) continue
      const { sourceFieldKey, operator, value, action } = field.conditionalRule

      if (!visibleFields.has(sourceFieldKey)) {
        if (action === 'show') {
          if (visibleFields.has(field.fieldId)) {
            visibleFields.delete(field.fieldId)
            changed = true
          }
        } else {
          if (!visibleFields.has(field.fieldId)) {
            visibleFields.add(field.fieldId)
            changed = true
          }
        }
        continue
      }

      const sourceValue = responses[sourceFieldKey]
      let conditionMet = false
      switch (operator) {
        case 'equals':
          conditionMet = sourceValue === value
          break
        case 'not_equals':
          conditionMet = sourceValue !== value
          break
        case 'contains':
          conditionMet = Array.isArray(sourceValue) && sourceValue.includes(value)
          break
        case 'not_empty':
          conditionMet =
            sourceValue != null && sourceValue !== '' && (!Array.isArray(sourceValue) || sourceValue.length > 0)
          break
        case 'is_empty':
          conditionMet =
            sourceValue == null || sourceValue === '' || (Array.isArray(sourceValue) && sourceValue.length === 0)
          break
      }

      const shouldBeVisible = action === 'show' ? conditionMet : !conditionMet
      if (shouldBeVisible && !visibleFields.has(field.fieldId)) {
        visibleFields.add(field.fieldId)
        changed = true
      }
      if (!shouldBeVisible && visibleFields.has(field.fieldId)) {
        visibleFields.delete(field.fieldId)
        changed = true
      }
    }
    if (!changed) break
  }
  return visibleFields
}

// ── Component ────────────────────────────────────────────────────────────────

const FormSubmitView = () => {
  const theme = useTheme()
  const { id } = useParams<{ id: string }>()
  const { pinned } = useLayoutContext()
  const { data: form, isLoading, isError } = useForm(id || null)
  const submitForm = useSubmitForm()

  const [responses, setResponses] = useState<Record<string, any>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitted, setSubmitted] = useState(false)
  const [submissionResult, setSubmissionResult] = useState<any>(null)

  // ── ClickUp state ────────────────────────────────────────────────────────
  const [taskName, setTaskName] = useState('')
  const [briefDescription, setBriefDescription] = useState('')
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)

  const clickupEnabled = form?.clickupEnabled ?? false
  const hasListPattern = clickupEnabled && !!form?.clickupListPattern && !form?.clickupTargetListId

  const spacesQuery = useClickUpSpaces()
  const foldersQuery = useClickUpFolders(hasListPattern ? selectedSpaceId : null)
  const listsQuery = useClickUpLists(hasListPattern ? selectedFolderId : null)

  // Fetch spaces on demand when pattern routing is used
  useMemo(() => {
    if (hasListPattern && !spacesQuery.data && !spacesQuery.isFetching) {
      spacesQuery.refetch()
    }
  }, [hasListPattern]) // eslint-disable-line react-hooks/exhaustive-deps

  // Pattern-match list in the selected folder
  const matchedList = useMemo(() => {
    if (!hasListPattern || !listsQuery.data || !form?.clickupListPattern) return null
    const pattern = form.clickupListPattern.toLowerCase()
    return listsQuery.data.find(l => l.name.toLowerCase().includes(pattern)) ?? null
  }, [hasListPattern, listsQuery.data, form?.clickupListPattern])

  const matchedListId = matchedList?.id ?? null

  // ── Conditional visibility ─────────────────────────────────────────────

  const visibleFields = useMemo(() => evaluateFieldVisibility(form?.fields || [], responses), [form?.fields, responses])

  // ── Update a field value ─────────────────────────────────────────────────

  const setValue = useCallback((fieldId: string, value: any) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }))
    setErrors(prev => {
      const next = { ...prev }
      delete next[fieldId]
      return next
    })
  }, [])

  // ── Reset form ─────────────────────────────────────────────────────────

  const resetForm = useCallback(() => {
    setSubmitted(false)
    setSubmissionResult(null)
    setResponses({})
    setErrors({})
    setTaskName('')
    setBriefDescription('')
    setSelectedSpaceId(null)
    setSelectedFolderId(null)
  }, [])

  // ── Validate and submit ──────────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    if (!form || !id) return

    const newErrors: Record<string, string> = {}

    // ClickUp validation
    if (clickupEnabled) {
      if (!taskName.trim()) {
        newErrors._taskName = 'Task name is required'
      }
      if (hasListPattern && !matchedListId) {
        newErrors._clickup = 'Please select a space and folder with a matching list'
      }
    }

    // Validate required fields — only visible ones
    for (const field of form.fields || []) {
      if (!visibleFields.has(field.fieldId)) continue
      if (field.required) {
        const val = responses[field.fieldId]
        if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
          newErrors[field.fieldId] = 'This field is required'
        }
      }
      // Email validation
      if (field.type === 'email' && responses[field.fieldId]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(responses[field.fieldId])) {
          newErrors[field.fieldId] = 'Please enter a valid email'
        }
      }
      // URL validation
      if (field.type === 'url' && responses[field.fieldId]) {
        try {
          new URL(responses[field.fieldId])
        } catch {
          newErrors[field.fieldId] = 'Please enter a valid URL'
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    // Only include visible field responses
    const filteredResponses: Record<string, any> = {}
    for (const field of form.fields || []) {
      if (visibleFields.has(field.fieldId) && responses[field.fieldId] !== undefined) {
        filteredResponses[field.fieldId] = responses[field.fieldId]
      }
    }

    try {
      const result = await submitForm.mutateAsync({
        formId: id,
        responses: filteredResponses,
        ...(clickupEnabled && {
          listId: matchedListId || form.clickupTargetListId,
          taskName: taskName.trim(),
          description: briefDescription.trim() || undefined,
        }),
      })
      setSubmissionResult(result)
      setSubmitted(true)
    } catch {
      setErrors({ _form: 'Failed to submit form. Please try again.' })
    }
  }, [
    form,
    id,
    responses,
    submitForm,
    visibleFields,
    clickupEnabled,
    taskName,
    briefDescription,
    matchedListId,
    hasListPattern,
  ])

  // ── Loading / Error states ───────────────────────────────────────────────

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    )
  }

  if (isError || !form) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <Typography color="error">Form not found or unavailable</Typography>
      </Box>
    )
  }

  if (form.status !== 'active') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh" flexDirection="column" gap={1}>
        <Typography variant="h6" color="text.secondary">
          This form is not currently accepting responses
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Contact the form owner for more information.
        </Typography>
      </Box>
    )
  }

  // ── Success ──────────────────────────────────────────────────────────────

  if (submitted) {
    const clickUpTask = submissionResult?.clickUpTask
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh" flexDirection="column" gap={2}>
        <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main' }} />
        <Typography variant="h5" fontWeight={600}>
          Thank you!
        </Typography>
        <Typography color="text.secondary">
          {form.completionMessage || 'Your response has been submitted successfully.'}
        </Typography>

        {clickUpTask && (
          <Box textAlign="center">
            <Typography variant="body2" color="text.secondary" mb={1}>
              ClickUp task created: {clickUpTask.name}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNewIcon />}
              href={clickUpTask.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in ClickUp
            </Button>
          </Box>
        )}

        <Button variant="outlined" onClick={resetForm}>
          Submit another response
        </Button>
      </Box>
    )
  }

  // ── Step state ──────────────────────────────────────────────────────────
  const step1Complete = clickupEnabled ? !!form.clickupTargetListId || !!matchedListId : true
  const step2Complete = clickupEnabled ? !!taskName.trim() : true

  const stepCircle = (num: number, complete: boolean) => (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: complete ? 'success.main' : 'warning.main',
        color: '#fff',
        fontWeight: 700,
        fontSize: 14,
        mr: 1.5,
        flexShrink: 0,
      }}
    >
      {complete ? <CheckCircleOutlineIcon sx={{ fontSize: 20 }} /> : num}
    </Box>
  )

  // ── Form ─────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        py: 4,
        px: 2,
        minHeight: '100vh',
        maxHeight: '100vh',
        overflowY: 'auto',
        paddingTop: pinned ? '60px' : 0,
        transition: 'padding-top 0.3s ease',
        bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.default : 'grey.100',
      }}
    >
      <Box sx={{ maxWidth: 800, width: '100%' }}>
        {/* Header */}
        <Typography variant="h5" fontWeight={700} mb={0.5}>
          {form.name}
        </Typography>
        {form.description && (
          <Typography variant="body2" color="text.secondary" mb={3}>
            {form.description}
          </Typography>
        )}

        {errors._form && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors._form}
          </Alert>
        )}

        <Stack spacing={2.5}>
          {/* ── Step 1: Select Client (ClickUp Context) ─────────────────── */}
          {clickupEnabled && (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderColor: step1Complete ? 'success.main' : 'divider',
                borderWidth: step1Complete ? 2 : 1,
                transition: 'border-color 0.3s ease',
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                {stepCircle(1, step1Complete)}
                <Typography variant="subtitle1" fontWeight={600}>
                  Select Client
                </Typography>
              </Box>

              {form.clickupTargetListId ? (
                <Alert icon={<InfoOutlinedIcon />} severity="info" variant="outlined">
                  This form creates a task in: <strong>{form.clickupTargetListName || form.clickupTargetListId}</strong>
                </Alert>
              ) : hasListPattern ? (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth error={!!errors._clickup}>
                    <InputLabel size="small">Group (Space)</InputLabel>
                    <Select
                      size="small"
                      value={selectedSpaceId || ''}
                      label="Group (Space)"
                      onChange={e => {
                        setSelectedSpaceId(e.target.value || null)
                        setSelectedFolderId(null)
                      }}
                    >
                      {(spacesQuery.data || []).map(space => (
                        <MenuItem key={space.id} value={space.id}>
                          {space.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth disabled={!selectedSpaceId}>
                    <InputLabel size="small">Client (Folder)</InputLabel>
                    <Select
                      size="small"
                      value={selectedFolderId || ''}
                      label="Client (Folder)"
                      onChange={e => setSelectedFolderId(e.target.value || null)}
                    >
                      {foldersQuery.isLoading && <MenuItem disabled>Loading...</MenuItem>}
                      {(foldersQuery.data || []).map(folder => (
                        <MenuItem key={folder.id} value={folder.id}>
                          {folder.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              ) : null}

              {selectedFolderId && (
                <Box mt={1.5}>
                  {listsQuery.isLoading ? (
                    <Typography variant="body2" color="text.secondary">
                      Finding matching list...
                    </Typography>
                  ) : matchedList ? (
                    <Alert severity="success" variant="outlined" sx={{ py: 0.5 }}>
                      Matched list: <strong>{matchedList.name}</strong>
                    </Alert>
                  ) : (
                    <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                      No matching list found for pattern &quot;{form.clickupListPattern}&quot;
                    </Alert>
                  )}
                </Box>
              )}

              {errors._clickup && (
                <FormHelperText error sx={{ mt: 1 }}>
                  {errors._clickup}
                </FormHelperText>
              )}
            </Paper>
          )}

          {/* ── Step 2: Request Details ──────────────────────────────────── */}
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderColor:
                step2Complete && Object.keys(errors).filter(k => !k.startsWith('_')).length === 0
                  ? 'success.main'
                  : 'divider',
              borderWidth: step2Complete ? 2 : 1,
              transition: 'border-color 0.3s ease',
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              {stepCircle(clickupEnabled ? 2 : 1, step2Complete)}
              <Typography variant="subtitle1" fontWeight={600}>
                Request Details
              </Typography>
            </Box>

            <Stack spacing={2.5}>
              {/* Task Name (when ClickUp enabled) */}
              {clickupEnabled && (
                <TextField
                  label="Task Name"
                  required
                  size="small"
                  placeholder="Brief title for your request..."
                  value={taskName}
                  onChange={e => {
                    setTaskName(e.target.value)
                    setErrors(prev => {
                      const n = { ...prev }
                      delete n._taskName
                      return n
                    })
                  }}
                  error={!!errors._taskName}
                  helperText={errors._taskName}
                  fullWidth
                />
              )}

              {/* Description/Comments */}
              {clickupEnabled && (
                <TextField
                  label="Comments"
                  placeholder="Describe what you need in detail. Include any relevant context or specifications..."
                  value={briefDescription}
                  onChange={e => setBriefDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                />
              )}

              {/* Dynamic Fields */}
              {(form.fields || [])
                .filter(field => visibleFields.has(field.fieldId))
                .map(field => (
                  <FieldRenderer
                    key={field.fieldId}
                    field={field}
                    value={responses[field.fieldId]}
                    error={errors[field.fieldId]}
                    onChange={val => setValue(field.fieldId, val)}
                  />
                ))}
            </Stack>
          </Paper>

          {/* ── Submit ───────────────────────────────────────────────────── */}
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitForm.isPending}
            fullWidth
            sx={{
              py: 1.5,
              fontWeight: 600,
              fontSize: '1rem',
              bgcolor: 'warning.main',
              color: '#000',
              '&:hover': { bgcolor: 'warning.dark' },
            }}
          >
            {submitForm.isPending ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} color="inherit" />
                Submitting...
              </Box>
            ) : clickupEnabled ? (
              'Submit Request'
            ) : (
              'Submit'
            )}
          </Button>
        </Stack>
      </Box>
    </Box>
  )
}

// ── Field Renderer ───────────────────────────────────────────────────────────

interface FieldRendererProps {
  field: FormField
  value: any
  error?: string
  onChange: (value: any) => void
}

const FieldRenderer = ({ field, value, error, onChange }: FieldRendererProps) => {
  switch (field.type) {
    case 'textarea':
      return (
        <TextField
          label={field.label}
          placeholder={field.placeholder}
          helperText={error || field.helpText}
          error={!!error}
          required={field.required}
          value={value ?? field.defaultValue ?? ''}
          onChange={e => onChange(e.target.value)}
          fullWidth
          multiline
          rows={4}
        />
      )

    case 'select':
      return (
        <FormControl fullWidth error={!!error} required={field.required}>
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={value ?? field.defaultValue ?? ''}
            label={field.label}
            onChange={e => onChange(e.target.value)}
          >
            {(field.options || []).map(opt => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
          {(error || field.helpText) && <FormHelperText>{error || field.helpText}</FormHelperText>}
        </FormControl>
      )

    case 'multiselect':
      return (
        <FormControl fullWidth error={!!error} required={field.required}>
          <InputLabel>{field.label}</InputLabel>
          <Select
            multiple
            value={value ?? []}
            label={field.label}
            onChange={e => onChange(e.target.value)}
            renderValue={(selected: string[]) => (
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {selected.map(v => (
                  <Chip key={v} label={v} size="small" />
                ))}
              </Stack>
            )}
          >
            {(field.options || []).map(opt => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
          {(error || field.helpText) && <FormHelperText>{error || field.helpText}</FormHelperText>}
        </FormControl>
      )

    case 'checkbox':
      return (
        <Box>
          <FormControlLabel
            control={<Checkbox checked={!!value} onChange={e => onChange(e.target.checked)} />}
            label={
              <Typography>
                {field.label} {field.required && '*'}
              </Typography>
            }
          />
          {(error || field.helpText) && (
            <FormHelperText error={!!error} sx={{ mt: -0.5, ml: 4 }}>
              {error || field.helpText}
            </FormHelperText>
          )}
        </Box>
      )

    case 'number':
      return (
        <TextField
          label={field.label}
          placeholder={field.placeholder}
          helperText={error || field.helpText}
          error={!!error}
          required={field.required}
          type="number"
          value={value ?? field.defaultValue ?? ''}
          onChange={e => onChange(e.target.value)}
          fullWidth
        />
      )

    case 'date':
      return (
        <TextField
          label={field.label}
          helperText={error || field.helpText}
          error={!!error}
          required={field.required}
          type="date"
          value={value ?? field.defaultValue ?? ''}
          onChange={e => onChange(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      )

    case 'email':
      return (
        <TextField
          label={field.label}
          placeholder={field.placeholder || 'email@example.com'}
          helperText={error || field.helpText}
          error={!!error}
          required={field.required}
          type="email"
          value={value ?? field.defaultValue ?? ''}
          onChange={e => onChange(e.target.value)}
          fullWidth
        />
      )

    case 'url':
      return (
        <TextField
          label={field.label}
          placeholder={field.placeholder || 'https://...'}
          helperText={error || field.helpText}
          error={!!error}
          required={field.required}
          type="url"
          value={value ?? field.defaultValue ?? ''}
          onChange={e => onChange(e.target.value)}
          fullWidth
        />
      )

    // text (default)
    default:
      return (
        <TextField
          label={field.label}
          placeholder={field.placeholder}
          helperText={error || field.helpText}
          error={!!error}
          required={field.required}
          value={value ?? field.defaultValue ?? ''}
          onChange={e => onChange(e.target.value)}
          fullWidth
        />
      )
  }
}

export default FormSubmitView
